import React from "react";
import { cn } from "utils";
import { Crypto } from "../Find/Crypto";
import { Hot } from "../Find/Hot";
import { EmptyState, LoadingState } from "./components";
import { CategoryType, TAB_ITEMS } from "./constants";
import Translations from "@translate/components/QuickInsights/RecommendedArea";
import { OpenDataTokenResult } from "@/src/hooks";
import { StockResult } from "@/src/hooks/useStockDashboard";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface RecommendedAreaProps {
  recommendedTab: "crypto" | "hk-stocks" | "us-stocks";
  onTabChange: (tab: "crypto" | "hk-stocks" | "us-stocks") => void;
  hotPageData: any[];
  hotPageDataLoading: boolean;
  trendingProjectsDataSet: any[];
  hotXRequestAgentLoading: boolean;
  stockTrending: any[];
  stockTrendingRequestAgentLoading: boolean;
  onItemClick: (item: any, category?: CategoryType) => void;
  title?: string;
}

export const RecommendedArea: React.FC<RecommendedAreaProps> = ({
  recommendedTab,
  onTabChange,
  hotPageData,
  hotPageDataLoading,
  trendingProjectsDataSet,
  hotXRequestAgentLoading,
  stockTrending,
  stockTrendingRequestAgentLoading,
  onItemClick,
  title,
}) => {
  return (
    <div className="flex flex-col gap-6 pb-6">
      {/* Tab 切换 - 仅在推荐内容区域显示 */}
      <div
        className={cn(
          "flex justify-start border-b border-border_white_transparent10 w-full sticky top-0 bg-background_solid_secondary z-10",
        )}
      >
        <tab-indicator
          className="flex gap-6 overflow-x-auto scrollbar-hide [--underline-color:var(--edgen-border\_brand\_solid)] [--animation-duration:0.2s]"
          effect="underline"
        >
          {TAB_ITEMS.map((item) => (
            <div
              data-openreplay-label="QuickInsights · RecommendedArea · div"
              key={item.key}
              role="tab-item"
              data-actived={recommendedTab === item.key}
              className="tab_item flex-shrink-0 cursor-pointer focus:outline-none"
              onClick={() => onTabChange(item.key)}
            >
              <span className="py-[6px] button_l text-content_primary">
                {item.label}
              </span>
            </div>
          ))}
        </tab-indicator>
      </div>

      {/* Hot 部分 - 热门股票/加密货币 */}
      <div className="flex flex-col gap-3 w-full">
        <div className="flex justify-between items-center">
          <span className="body_medium_s text-content_tertiary font-medium">
            {recommendedTab === "crypto" ? I18n.t("Hot") : I18n.t("Big Tech")}
          </span>
        </div>
        {hotPageDataLoading && (
          <LoadingState
            count={6}
            className={cn("grid-cols-1 md:grid-cols-3")}
          />
        )}
        {!hotPageDataLoading && hotPageData.length > 0 && (
          <div className={cn("grid grid-cols-1 md:grid-cols-3 gap-3")}>
            {hotPageData.slice(0, 6).map((item, index) => (
              <div
                data-openreplay-label="QuickInsights · RecommendedArea · div"
                key={index}
                onClick={() =>
                  onItemClick(
                    item,
                    recommendedTab === "crypto"
                      ? CategoryType.CRYPTO
                      : CategoryType.STOCKS,
                  )
                }
                data-tracking-scope={`${title}-${recommendedTab}-recommend-question-symbol-${item?.symbol}`}
                className="bg-background_white_overlay5 hover:bg-background_white_overlay10 rounded-xl cursor-pointer transition-colors duration-200"
              >
                <Crypto
                  data={item as OpenDataTokenResult & StockResult}
                  disableSummary={true}
                  type={recommendedTab}
                />
              </div>
            ))}
          </div>
        )}
        {!hotPageDataLoading && hotPageData.length === 0 && <EmptyState />}
      </div>

      {/* crypto 趋势项目部分 */}
      {recommendedTab === "crypto" && (
        <div className="flex flex-col gap-3 w-full">
          <div className="flex justify-between items-center">
            <span className="body_medium_s text-content_tertiary font-medium">
              {I18n.t("Trending Projects")}
            </span>
          </div>
          {hotXRequestAgentLoading && trendingProjectsDataSet.length === 0 && (
            <LoadingState count={6} className="grid-cols-1 md:grid-cols-3" />
          )}
          {!hotXRequestAgentLoading && trendingProjectsDataSet.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 sm:grid-cols-1 gap-4">
              {trendingProjectsDataSet.map((item: any, index: number) => (
                <div
                  data-openreplay-label="QuickInsights · RecommendedArea · div"
                  key={index}
                  onClick={() => onItemClick(item, CategoryType.CRYPTO)}
                  data-tracking-scope={`${title}-${recommendedTab}-recommend-question-project-${item?.token_symbol}-${item?.project_name}`}
                  className="bg-background_white_overlay5 hover:bg-background_white_overlay10 rounded-xl cursor-pointer transition-colors duration-200"
                >
                  <Hot data={item} />
                </div>
              ))}
            </div>
          )}
          {!hotXRequestAgentLoading && trendingProjectsDataSet.length === 0 && (
            <EmptyState />
          )}
        </div>
      )}

      {/* stock 趋势部分 */}
      {(recommendedTab === "hk-stocks" || recommendedTab === "us-stocks") && (
        <div className="flex flex-col gap-3 w-full">
          <div className="flex justify-between items-center">
            <span className="body_medium_s text-content_tertiary font-medium">
              {I18n.t("Trending")}
            </span>
          </div>
          {stockTrendingRequestAgentLoading && stockTrending.length === 0 && (
            <LoadingState count={6} className="grid-cols-1 md:grid-cols-3" />
          )}
          {!stockTrendingRequestAgentLoading && stockTrending.length > 0 && (
            <div className={cn("grid grid-cols-1 md:grid-cols-3 gap-3")}>
              {stockTrending.slice(0, 6).map((item, index) => (
                <div
                  data-openreplay-label="QuickInsights · RecommendedArea · div"
                  key={index}
                  onClick={() => onItemClick(item, CategoryType.STOCKS)}
                  data-tracking-scope={`${title}-${recommendedTab}-recommend-question-symbol-${item?.symbol}`}
                  className="bg-background_white_overlay5 hover:bg-background_white_overlay10 rounded-xl cursor-pointer transition-colors duration-200"
                >
                  <Crypto
                    data={item as OpenDataTokenResult & StockResult}
                    disableSummary={true}
                    type={recommendedTab}
                  />
                </div>
              ))}
            </div>
          )}
          {!stockTrendingRequestAgentLoading && stockTrending.length === 0 && (
            <EmptyState />
          )}
        </div>
      )}
    </div>
  );
};
